/**
 * Configuración de facturación SaaS — plataforma FM.
 * Incluye descuento anual + datos de transferencia bancaria ARS y USD.
 */
import { sql } from "@/lib/db";
import { normalizeAnnualDiscountPct, yearlyPriceFromMonthly } from "@/lib/saas-billing-math";

export { normalizeAnnualDiscountPct, yearlyPriceFromMonthly };

export interface SaasBankTransfer {
  holder: string;        // Nombre del titular
  cbu: string;
  alias: string;
  bank: string;          // Nombre del banco
  notes?: string;        // Instrucciones adicionales
}

/** Cuenta USD nacional argentina (no wire internacional): CBU/alias en banco local. */
export interface SaasBankTransferUsd {
  holder: string;        // Titular
  cbu: string;           // CBU en dólares
  alias: string;
  bank: string;          // Nombre del banco
  instructions?: string; // Instrucciones para la transferencia
}

/** Pasarela que Structura usa para cobrar suscripciones SaaS a los tenants. */
export type SubscriptionGateway = "mercadopago" | "payway" | "uala" | "dlocal";

/** Pasarelas para packs (créditos add-on), en orden de prioridad. Nunca MP. */
export type PackGateway = "payway" | "uala";

/** SCRUM-225: prioridad opcional por operación × método (crédito/débito/transfer). */
export type SaasOperationMethodPriorityConfig = import("@/lib/saas-payment-routing").SaasOperationMethodPriority;

export interface SaasSelfServiceConfig {
  enabled: boolean;
  gateways: {
    payway: boolean;
    uala: boolean;
    dlocal: boolean;
    manualTransferUsd: boolean;
    manualTransferArs: boolean;
  };
  /**
   * Gateway preferido para cobrar suscripciones (Structura → tenant). Si está
   * configurado y conectado, se prioriza sobre el resto en `gatewayOrder`
   * (nunca se excluyen los demás por completo — sirven de respaldo si el
   * preferido no está disponible).
   */
  subscriptionPreferredGateway: SubscriptionGateway;
  /**
   * Orden de prioridad para cobrar packs (créditos add-on). El primero es el
   * que se ofrece por defecto en el link de pago; si falla y el siguiente
   * está en la lista, se ofrece como reintento automático antes de fallar.
   */
  packGatewayPriority: PackGateway[];
  /**
   * SCRUM-225 — prioridad explícita por operación y método de checkout.
   * Si falta, se deriva de subscriptionPreferredGateway / packGatewayPriority.
   */
  operationMethodPriority?: SaasOperationMethodPriorityConfig;
  /** SCRUM-1042: ofrecer 24 y 48 meses en el alta online (saas-backend lo lee de acá). Apagado por defecto. */
  multiYearPeriods?: boolean;
}

/**
 * SCRUM-1031 — prueba gratis de plataforma. La lee saas-backend (`/v1/self-service/config`, gate de `start` y
 * `request_trial`) y el sitio corporativo. Apagada salvo `enabled: true` explícito.
 *
 * SCRUM-1037 — `mode` decide cómo se entra (default `assisted`):
 *  - self_service: la cuenta se crea apenas la persona la pide.
 *  - assisted: cada solicitud espera tu aprobación en la bandeja del backoffice; la persona arma su cuenta sola.
 *  - full_service: también espera la bandeja, y el equipo arma la cuenta con la persona.
 * `approval` se deriva del modo (lo lee saas-backend por compatibilidad).
 */
export type SaasTrialMode = "self_service" | "assisted" | "full_service";
export const TRIAL_MODES: readonly SaasTrialMode[] = ["self_service", "assisted", "full_service"];
export const TRIAL_MODE_LABEL: Record<SaasTrialMode, string> = {
  self_service: "Self-service: acceso inmediato",
  assisted: "Asistido: apruebo cada solicitud",
  full_service: "Full service: apruebo y la armamos juntos",
};

export interface SaasTrialConfig {
  enabled: boolean;
  days: number;
  mode: SaasTrialMode;
  approval: "auto" | "manual";
  /** SCRUM-1035: confirmar el email con un link antes de llegar a la bandeja (default prendido). */
  verifyEmail: boolean;
  /** SCRUM-1035: captcha Cloudflare Turnstile al pedir la prueba (default apagado; requiere la site key en el sitio). */
  captcha: boolean;
}

export const TRIAL_DAYS_DEFAULT = 14;
export const TRIAL_DAYS_MIN = 7;
export const TRIAL_DAYS_MAX = 30;

export function parseTrialConfig(raw: unknown): SaasTrialConfig {
  const t = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const n = Math.round(Number(t.days));
  const days = Number.isFinite(n) && n > 0 ? Math.min(TRIAL_DAYS_MAX, Math.max(TRIAL_DAYS_MIN, n)) : TRIAL_DAYS_DEFAULT;
  const mode: SaasTrialMode = TRIAL_MODES.includes(t.mode as SaasTrialMode) ? (t.mode as SaasTrialMode) : "assisted";
  return { enabled: t.enabled === true, days, mode, approval: mode === "self_service" ? "auto" : "manual", verifyEmail: t.verifyEmail !== false, captcha: t.captcha === true };
}

export interface SaasBillingConfig {
  annualDiscountPct: number;
  trial?: SaasTrialConfig;
  bankTransferArs?: SaasBankTransfer | null;
  bankTransferUsd?: SaasBankTransferUsd | null;
  selfService?: SaasSelfServiceConfig;
}

const DEFAULT_SELF_SERVICE: SaasSelfServiceConfig = {
  enabled: true,
  gateways: { payway: true, uala: true, dlocal: true, manualTransferUsd: true, manualTransferArs: false },
  subscriptionPreferredGateway: "mercadopago",
  packGatewayPriority: ["payway", "uala"],
};

const DEFAULT: SaasBillingConfig = { annualDiscountPct: 25, selfService: DEFAULT_SELF_SERVICE };

/**
 * Process overlay for the admin kill-switch (`billing_config.selfService.enabled`).
 *
 * Production SoT remains `sm_platform_settings` (operator toggle in /admin).
 * `STRUCTURA_SELF_SERVICE_ENABLED=true|false` overrides for this process only
 * (local / E2E). It does not write the database.
 */
export function resolveSelfServiceEnabledFromEnv(
  storedEnabled: boolean,
  envValue: string | undefined = process.env.STRUCTURA_SELF_SERVICE_ENABLED,
): boolean {
  const v = (envValue ?? "").trim().toLowerCase();
  if (v === "true" || v === "1" || v === "on" || v === "yes") return true;
  if (v === "false" || v === "0" || v === "off" || v === "no") return false;
  return storedEnabled;
}

/**
 * `/contratar` session start gate. Paid plans: same authority as
 * `billing_config.selfService.enabled` (plus overlay on public reads).
 * Trial: SCRUM-1031 `billing_config.trial.enabled` (off by default), independent of the paid switch.
 * Super-admin may start either for ops tests.
 */
export function mayStartPaidSelfServiceSession(input: {
  selfServiceEnabled: boolean;
  isSaasAdmin: boolean;
  planId: string;
  trialEnabled?: boolean;
}): boolean {
  if (input.planId === "trial") return input.isSaasAdmin || input.trialEnabled === true;
  if (input.isSaasAdmin) return true;
  return input.selfServiceEnabled !== false;
}

const VALID_SUBSCRIPTION_GATEWAYS: SubscriptionGateway[] = ["mercadopago", "payway", "uala", "dlocal"];
const VALID_PACK_GATEWAYS: PackGateway[] = ["payway", "uala"];

function parseSubscriptionGateway(raw: unknown): SubscriptionGateway {
  return VALID_SUBSCRIPTION_GATEWAYS.includes(raw as SubscriptionGateway)
    ? (raw as SubscriptionGateway)
    : DEFAULT_SELF_SERVICE.subscriptionPreferredGateway;
}

function parsePackGatewayPriority(raw: unknown): PackGateway[] {
  if (!Array.isArray(raw)) return [...DEFAULT_SELF_SERVICE.packGatewayPriority];
  const seen = new Set<PackGateway>();
  const out: PackGateway[] = [];
  for (const v of raw) {
    if (VALID_PACK_GATEWAYS.includes(v as PackGateway) && !seen.has(v as PackGateway)) {
      seen.add(v as PackGateway);
      out.push(v as PackGateway);
    }
  }
  return out.length ? out : [...DEFAULT_SELF_SERVICE.packGatewayPriority];
}

function parseStoredSelfService(raw: unknown): SaasSelfServiceConfig {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_SELF_SERVICE };
  const r = raw as Record<string, unknown>;
  const g = (r.gateways && typeof r.gateways === "object" ? r.gateways : {}) as Record<string, unknown>;
  const operationMethodPriority =
    r.operationMethodPriority && typeof r.operationMethodPriority === "object"
      ? (r.operationMethodPriority as SaasOperationMethodPriorityConfig)
      : undefined;
  return {
    enabled: r.enabled !== false,
    gateways: {
      payway: g.payway !== false,
      uala: g.uala !== false,
      dlocal: g.dlocal !== false,
      manualTransferUsd: g.manualTransferUsd !== false,
      manualTransferArs: g.manualTransferArs === true,
    },
    subscriptionPreferredGateway: parseSubscriptionGateway(r.subscriptionPreferredGateway),
    packGatewayPriority: parsePackGatewayPriority(r.packGatewayPriority),
    ...(operationMethodPriority ? { operationMethodPriority } : {}),
    multiYearPeriods: r.multiYearPeriods === true,
  };
}

function withSelfServiceOverlay(stored: SaasSelfServiceConfig): SaasSelfServiceConfig {
  return { ...stored, enabled: resolveSelfServiceEnabledFromEnv(stored.enabled) };
}

export async function dbSmGetBillingConfig(opts?: { overlay?: boolean }): Promise<SaasBillingConfig> {
  const applyOverlay = opts?.overlay !== false;
  try {
    const db = sql();
    const rows = await db`
      SELECT billing_config FROM sm_platform_settings WHERE id = 'default' LIMIT 1
    `;
    const cfg = rows[0]?.billing_config as Record<string, unknown> | undefined;
    const stored = parseStoredSelfService(cfg?.selfService);
    return {
      annualDiscountPct: normalizeAnnualDiscountPct(cfg?.annualDiscountPct),
      bankTransferArs: (cfg?.bankTransferArs as SaasBankTransfer | null) ?? null,
      bankTransferUsd: (cfg?.bankTransferUsd as SaasBankTransferUsd | null) ?? null,
      selfService: applyOverlay ? withSelfServiceOverlay(stored) : stored,
      trial: parseTrialConfig(cfg?.trial),
    };
  } catch {
    return {
      ...DEFAULT,
      selfService: applyOverlay ? withSelfServiceOverlay(DEFAULT_SELF_SERVICE) : { ...DEFAULT_SELF_SERVICE },
      trial: parseTrialConfig(null),
    };
  }
}

export async function dbSmSetBillingConfig(patch: Partial<SaasBillingConfig>): Promise<SaasBillingConfig> {
  const db = sql();
  const rows = await db`
    SELECT billing_config FROM sm_platform_settings WHERE id = 'default' LIMIT 1
  `;
  const stored = (rows[0]?.billing_config as Record<string, unknown> | undefined) ?? {};
  const curSelf = parseStoredSelfService(stored.selfService);
  const extras = { ...stored };
  delete extras.annualDiscountPct;
  delete extras.bankTransferArs;
  delete extras.bankTransferUsd;
  delete extras.selfService;
  delete extras.trial;
  const next = {
    ...extras,
    annualDiscountPct: patch.annualDiscountPct ?? normalizeAnnualDiscountPct(stored.annualDiscountPct),
    bankTransferArs: patch.bankTransferArs !== undefined ? patch.bankTransferArs : ((stored.bankTransferArs as SaasBankTransfer | null) ?? null),
    bankTransferUsd: patch.bankTransferUsd !== undefined ? patch.bankTransferUsd : ((stored.bankTransferUsd as SaasBankTransferUsd | null) ?? null),
    selfService: patch.selfService !== undefined ? parseStoredSelfService(patch.selfService) : curSelf,
    trial: parseTrialConfig(patch.trial !== undefined ? patch.trial : stored.trial),
  };
  await db`
    INSERT INTO sm_platform_settings (id, billing_config, updated_at)
    VALUES ('default', ${JSON.stringify(next)}, NOW())
    ON CONFLICT (id) DO UPDATE SET billing_config = EXCLUDED.billing_config, updated_at = NOW()
  `;
  return {
    ...next,
    selfService: withSelfServiceOverlay(next.selfService ?? DEFAULT_SELF_SERVICE),
  };
}

